/**
 * Roles, accounts, and your own password.
 *
 * Its own module rather than another wing of `client.ts`, which is already
 * long and is about the travel catalogue — this is about who may touch it.
 *
 * Every call here is refused by the server unless the caller holds the
 * matching permission, so nothing in this file needs to check anything. The
 * console hides the panels an operator cannot use, but that is tidiness; the
 * refusal is what enforces it.
 */
import { rpc } from './transport';
import {
  listRoles as listRolesMethod,
  listPermissions as listPermissionsMethod,
  createRole as createRoleMethod,
  updateRole as updateRoleMethod,
  deleteRole as deleteRoleMethod,
} from '../proto/travplaces/v1/admin_role-AdminRoleService_connectquery';
import {
  listUsers as listUsersMethod,
  createUser as createUserMethod,
  setUserName as setUserNameMethod,
  setUserRole as setUserRoleMethod,
  resetUserPassword as resetUserPasswordMethod,
  deleteUser as deleteUserMethod,
} from '../proto/travplaces/v1/admin_user-AdminUserService_connectquery';
import { changePassword as changePasswordMethod } from '../proto/travplaces/v1/profile-ProfileService_connectquery';

/** A named set of permissions that accounts can hold. */
export interface Role {
  id: string;
  name: string;
  description: string;
  /** Seeded roles. Locked: see the note in admin_role.proto. */
  isSystem: boolean;
  /** Permission enum names, e.g. `PERMISSION_CITY_WRITE`. */
  permissions: string[];
  /** How many accounts hold it, so a delete can explain itself. */
  userCount: number;
}

/** One permission, with the wording to show an operator. */
export interface PermissionInfo {
  name: string;
  displayName: string;
  description: string;
  /** The permission that implies this one; empty at the root. */
  parent: string;
}

/** An operator account. Never carries a password, in either direction. */
export interface AccountUser {
  id: string;
  username: string;
  /** What to call them. Empty means none given: screens show the username. */
  displayName: string;
  roleId: string;
  roleName: string;
  /** The account making the request, which cannot delete itself. */
  isSelf: boolean;
}

/**
 * The minimum the server will accept. Mirrored here only so the form can say
 * so before a round trip — `auth.MinPasswordLength` is the real rule, and a
 * client that ignores this still gets refused.
 */
export const MIN_PASSWORD_LENGTH = 12;

export const accessApi = {
  roles: async (): Promise<Role[]> => {
    const res = await rpc(listRolesMethod, {});
    return res.roles.map((r) => ({
      id: r.id,
      name: r.name,
      description: r.description,
      isSystem: r.isSystem,
      permissions: [...r.permissions],
      userCount: r.userCount,
    }));
  },

  permissions: async (): Promise<PermissionInfo[]> => {
    const res = await rpc(listPermissionsMethod, {});
    return res.permissions.map((p) => ({
      name: p.name,
      displayName: p.displayName,
      description: p.description,
      parent: p.parent,
    }));
  },

  createRole: async (input: { name: string; description: string; permissions: string[] }) => {
    await rpc(createRoleMethod, input);
  },

  updateRole: async (
    input: { id: string; name: string; description: string; permissions: string[] },
  ) => {
    await rpc(updateRoleMethod, input);
  },

  deleteRole: async (id: string) => {
    await rpc(deleteRoleMethod, { id });
  },

  users: async (): Promise<AccountUser[]> => {
    const res = await rpc(listUsersMethod, {});
    return res.users.map((u) => ({
      id: u.id,
      username: u.username,
      displayName: u.displayName,
      roleId: u.roleId,
      roleName: u.roleName,
      isSelf: u.isSelf,
    }));
  },

  createUser: async (
    input: { username: string; password: string; roleId: string; displayName?: string },
  ) => {
    await rpc(createUserMethod, { displayName: '', ...input });
  },

  /**
   * Renames the person, not the login.
   *
   * An empty name is allowed and means "none given" — every screen then
   * falls back to the username, as it did before names existed.
   */
  setUserName: async (id: string, displayName: string) => {
    await rpc(setUserNameMethod, { id, displayName });
  },

  setUserRole: async (id: string, roleId: string) => {
    await rpc(setUserRoleMethod, { id, roleId });
  },

  resetUserPassword: async (id: string, password: string) => {
    await rpc(resetUserPasswordMethod, { id, password });
  },

  deleteUser: async (id: string) => {
    await rpc(deleteUserMethod, { id });
  },

  /** Changes the signed-in operator's own password. */
  changeOwnPassword: async (currentPassword: string, newPassword: string) => {
    await rpc(changePasswordMethod, { currentPassword, newPassword });
  },
};
